/**
 * Privacy Policy — The Chi Alliance
 * Visual direction: quiet editorial legal page with a deep-obisidian field, magenta-to-gold heading treatment,
 * and generous reading measure so the policy feels considered rather than like a generic document dump.
 */
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { Mail, ShieldCheck } from "lucide-react";

const informationWeCollect = [
  "Name",
  "Email address",
  "Company and job title",
  "Event registrations and attendance information",
  "Communication preferences",
  "Information submitted through contact forms, membership applications, sponsorship inquiries, or community participation",
];

const howWeUseInformation = [
  "Communicate with you about events, programs, and community activities",
  "Manage memberships, registrations, and partnerships",
  "Provide educational content and networking opportunities",
  "Improve our website, programs, and member experience",
  "Respond to inquiries and support requests",
  "Share relevant updates, newsletters, and announcements",
];

function PolicyList({ items }: { items: string[] }) {
  return (
    <ul className="privacy-policy__list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="privacy-policy-page">
      <Navigation />

      <main>
        <section className="privacy-policy__hero" aria-labelledby="privacy-policy-title">
          <div className="privacy-policy__hero-glow privacy-policy__hero-glow--magenta" aria-hidden="true" />
          <div className="privacy-policy__hero-glow privacy-policy__hero-glow--gold" aria-hidden="true" />
          <div className="container privacy-policy__hero-inner">
            <div className="privacy-policy__eyebrow">
              <ShieldCheck size={15} strokeWidth={1.8} aria-hidden="true" />
              <span>Privacy &amp; trust</span>
            </div>
            <h1 id="privacy-policy-title" className="privacy-policy__title">
              Our <span>Privacy Policy</span>
            </h1>
            <p className="privacy-policy__intro">
              How The Chi Alliance collects, uses, and protects the information entrusted to us through our community, programs, and events.
            </p>
          </div>
        </section>

        <section className="privacy-policy__content-section">
          <div className="container privacy-policy__layout">
            <aside className="privacy-policy__aside" aria-label="Privacy commitment">
              <div className="privacy-policy__aside-rule" aria-hidden="true" />
              <p className="privacy-policy__aside-label">Our commitment</p>
              <p className="privacy-policy__aside-copy">
                Your information helps us create thoughtful, people-first experiences. It is not a commodity.
              </p>
              <a className="privacy-policy__contact-link" href="mailto:info@thechialliance.com">
                <Mail size={15} strokeWidth={1.8} aria-hidden="true" />
                <span>Contact our team</span>
              </a>
            </aside>

            <article className="privacy-policy__document">
              <div className="privacy-policy__lead">
                <p>
                  At <strong>The CHI Alliance (Connecting Human Innovators)</strong>, we value your privacy and are committed to protecting the personal information you share with us. This Privacy Policy outlines how we collect, use, and safeguard your information when you engage with our website, events, community activities, and communications.
                </p>
              </div>

              <section className="privacy-policy__section" aria-labelledby="information-we-collect">
                <h2 id="information-we-collect">Information We Collect</h2>
                <p>We may collect personal information that you voluntarily provide, including:</p>
                <PolicyList items={informationWeCollect} />
              </section>

              <section className="privacy-policy__section" aria-labelledby="how-we-use-information">
                <h2 id="how-we-use-information">How We Use Your Information</h2>
                <p>We use your information to:</p>
                <PolicyList items={howWeUseInformation} />
              </section>

              <section className="privacy-policy__section" aria-labelledby="information-sharing">
                <h2 id="information-sharing">Information Sharing</h2>
                <p>
                  The CHI Alliance does not sell, rent, or trade your personal information. We may share information with trusted service providers and partners who support our operations, events, and communications, as required to provide our services.
                </p>
                <p>
                  For event ticket orders, attendee names and any email addresses provided are recorded with our payment processor, Stripe, alongside the checkout transaction to help reconcile purchases and support event operations.
                </p>
              </section>

              <section className="privacy-policy__section" aria-labelledby="data-security">
                <h2 id="data-security">Data Security</h2>
                <p>
                  We take reasonable administrative, technical, and organizational measures to protect your personal information. However, no method of electronic transmission or storage is completely secure.
                </p>
              </section>

              <section className="privacy-policy__section" aria-labelledby="event-ticket-handoff">
                <h2 id="event-ticket-handoff">External Event Ticket Handoff</h2>
                <p>
                  Only after a signed-in member explicitly chooses to open an externally hosted event ticket page, we use a short-lived, strictly necessary first-party cookie containing an opaque handoff identifier. It lets us fulfill that ticket-handoff request, record that checkout was opened, and maintain aggregate operations reporting. The cookie does not contain payment information, does not track your activity on the ticket provider’s website, and does not by itself indicate that a purchase was completed.
                </p>
              </section>

              <section className="privacy-policy__section" aria-labelledby="third-party-links">
                <h2 id="third-party-links">Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites, sponsors, partners, and social media platforms. We are not responsible for the privacy practices or content of these external sites.
                </p>
              </section>

              <section className="privacy-policy__section" aria-labelledby="your-choices">
                <h2 id="your-choices">Your Choices</h2>
                <p>
                  You may opt out of receiving communications from us at any time by following the unsubscribe instructions in our emails or contacting us directly.
                </p>
              </section>

              <section className="privacy-policy__section privacy-policy__section--contact" aria-labelledby="contact-us">
                <h2 id="contact-us">Contact Us</h2>
                <p>If you have any questions regarding this Privacy Policy, please contact us at:</p>
                <address className="privacy-policy__contact-address">
                  <strong>The CHI Alliance</strong>
                  <a href="mailto:info@thechialliance.com">info@thechialliance.com</a>
                  <a href="https://www.thechialliance.com" target="_blank" rel="noopener noreferrer">www.thechialliance.com</a>
                </address>
              </section>
            </article>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .privacy-policy-page {
          min-height: 100vh;
          overflow: hidden;
          color: #F8FAFC;
          background: #080C18;
          font-family: 'Inter', sans-serif;
        }

        .privacy-policy__hero {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background:
            radial-gradient(circle at 78% 0%, rgba(217, 61, 145, 0.17), transparent 28rem),
            radial-gradient(circle at 84% 104%, rgba(240, 168, 58, 0.14), transparent 27rem),
            linear-gradient(115deg, #080C18 12%, #0C1020 54%, #14101C 100%);
        }

        .privacy-policy__hero::after {
          position: absolute;
          z-index: -1;
          inset: 0;
          content: '';
          opacity: 0.4;
          background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 52px 52px;
          mask-image: linear-gradient(90deg, #000, transparent 87%);
        }

        .privacy-policy__hero-glow {
          position: absolute;
          z-index: -1;
          width: 22rem;
          height: 22rem;
          border-radius: 999px;
          filter: blur(76px);
          opacity: 0.3;
          pointer-events: none;
        }

        .privacy-policy__hero-glow--magenta { top: -13rem; right: 20%; background: #DC3D91; }
        .privacy-policy__hero-glow--gold { right: -6rem; bottom: -15rem; background: #F0A83A; }

        .privacy-policy__hero-inner {
          min-height: clamp(23rem, 43vw, 35rem);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding-top: clamp(8rem, 15vw, 12.5rem);
          padding-bottom: clamp(3.75rem, 8vw, 7rem);
        }

        .privacy-policy__eyebrow {
          display: inline-flex;
          width: fit-content;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.4rem;
          padding: 0.48rem 0.75rem;
          border: 1px solid rgba(220, 61, 145, 0.31);
          border-radius: 999px;
          color: #F8B5DA;
          background: rgba(220, 61, 145, 0.09);
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.69rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .privacy-policy__title {
          max-width: 820px;
          margin: 0;
          color: #FFFFFF;
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: 72px;
          font-weight: 400;
          letter-spacing: -0.052em;
          line-height: 0.93;
        }

        .privacy-policy__title span {
          display: inline-block;
          color: transparent;
          background: linear-gradient(100deg, #DC3D91 4%, #E36299 36%, #F0A83A 96%);
          background-clip: text;
          -webkit-background-clip: text;
        }

        .privacy-policy__intro {
          max-width: 620px;
          margin: 1.55rem 0 0;
          color: #CBD5E1;
          font-size: clamp(1rem, 1.35vw, 1.14rem);
          line-height: 1.75;
        }

        .privacy-policy__content-section {
          position: relative;
          padding: clamp(4rem, 8vw, 7.5rem) 0 clamp(5rem, 9vw, 8.5rem);
          background:
            linear-gradient(115deg, rgba(255,255,255,0.018) 1px, transparent 1px),
            linear-gradient(25deg, #080C18 0%, #0A0E1A 61%, #0B101B 100%);
          background-size: 42px 42px, auto;
        }

        .privacy-policy__layout {
          display: grid;
          grid-template-columns: minmax(0, 0.38fr) minmax(0, 1fr);
          gap: clamp(3rem, 8vw, 10rem);
          align-items: start;
        }

        .privacy-policy__aside {
          position: sticky;
          top: 7.5rem;
          max-width: 17rem;
          padding-top: 0.15rem;
        }

        .privacy-policy__aside-rule {
          width: 4rem;
          height: 2px;
          margin-bottom: 1.2rem;
          background: linear-gradient(90deg, #DC3D91, #F0A83A);
        }

        .privacy-policy__aside-label {
          margin: 0 0 0.85rem;
          color: #F8FAFC;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .privacy-policy__aside-copy {
          margin: 0;
          color: #94A3B8;
          font-size: 0.92rem;
          line-height: 1.75;
        }

        .privacy-policy__contact-link {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          margin-top: 1.6rem;
          color: #F8B5DA;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.81rem;
          font-weight: 600;
          text-decoration: none;
          transition: color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .privacy-policy__contact-link:hover { color: #FFFFFF; transform: translateX(3px); }
        .privacy-policy__contact-link:focus-visible { outline: 2px solid #F0A83A; outline-offset: 5px; border-radius: 3px; }

        .privacy-policy__document {
          max-width: 46rem;
          padding: clamp(1.55rem, 4vw, 3.2rem);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 1.4rem;
          background: linear-gradient(145deg, rgba(17,23,39,0.92), rgba(10,14,26,0.94));
          box-shadow: 0 30px 80px rgba(0,0,0,0.23);
        }

        .privacy-policy__lead {
          margin-bottom: 2.6rem;
          padding-bottom: 2.35rem;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          color: #D9E0EA;
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: clamp(1.22rem, 2vw, 1.48rem);
          line-height: 1.62;
        }

        .privacy-policy__lead p { margin: 0; }
        .privacy-policy__lead strong { color: #FFFFFF; font-weight: 400; }

        .privacy-policy__section + .privacy-policy__section { margin-top: 2.5rem; }

        .privacy-policy__section h2 {
          margin: 0 0 0.9rem;
          color: #FFFFFF;
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: clamp(1.72rem, 2.5vw, 2.24rem);
          font-weight: 400;
          letter-spacing: -0.026em;
          line-height: 1.08;
        }

        .privacy-policy__section p {
          margin: 0;
          color: #B8C4D3;
          font-size: 0.98rem;
          line-height: 1.8;
        }

        .privacy-policy__list {
          display: grid;
          gap: 0.76rem;
          margin: 1.25rem 0 0;
          padding: 0;
          list-style: none;
        }

        .privacy-policy__list li {
          position: relative;
          padding-left: 1.28rem;
          color: #B8C4D3;
          font-size: 0.97rem;
          line-height: 1.65;
        }

        .privacy-policy__list li::before {
          position: absolute;
          top: 0.66rem;
          left: 0;
          width: 0.42rem;
          height: 0.42rem;
          border-radius: 99px;
          content: '';
          background: linear-gradient(135deg, #DC3D91, #F0A83A);
          box-shadow: 0 0 10px rgba(220, 61, 145, 0.32);
        }

        .privacy-policy__section--contact {
          margin-top: 3rem !important;
          padding: 1.65rem;
          border: 1px solid rgba(240,168,58,0.2);
          border-radius: 1rem;
          background: linear-gradient(135deg, rgba(240,168,58,0.08), rgba(220,61,145,0.07));
        }

        .privacy-policy__contact-address {
          display: grid;
          gap: 0.36rem;
          margin-top: 1.15rem;
          color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.92rem;
          font-style: normal;
          line-height: 1.55;
        }

        .privacy-policy__contact-address a {
          width: fit-content;
          color: #F8B5DA;
          text-decoration: none;
          transition: color 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .privacy-policy__contact-address a:hover { color: #FFFFFF; }
        .privacy-policy__contact-address a:focus-visible { outline: 2px solid #F0A83A; outline-offset: 3px; border-radius: 2px; }

        @media (max-width: 860px) {
          .privacy-policy__hero-inner { min-height: 26rem; }
          .privacy-policy__layout { grid-template-columns: 1fr; gap: 2.5rem; }
          .privacy-policy__aside { position: static; max-width: 34rem; }
          .privacy-policy__document { max-width: none; }
        }

        @media (max-width: 560px) {
          .privacy-policy__hero-inner { min-height: 24rem; padding-top: 7.2rem; }
          .privacy-policy__title { font-size: clamp(3rem, 15vw, 4.5rem); }
          .privacy-policy__document { padding: 1.35rem; border-radius: 1rem; }
          .privacy-policy__lead { margin-bottom: 2.1rem; padding-bottom: 1.85rem; }
          .privacy-policy__section + .privacy-policy__section { margin-top: 2.15rem; }
          .privacy-policy__section--contact { padding: 1.3rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          .privacy-policy__contact-link,
          .privacy-policy__contact-address a { transition: none; }
        }
      `}</style>
    </div>
  );
}
